import { useCallback } from "react"
import { useMutation } from "urql"

import { useCreateAdjustmentAttachments } from "./useCreateAdjustmentAttachments"

import type {
  CreateInventoryAdjustmentFailure,
  CreateInventoryAdjustmentSuccess,
  InventoryAdjustment,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

type AdjustmentCreateOutput =
  | CreateInventoryAdjustmentFailure
  | {
      __typename?: "CreateInventoryAdjustmentSuccess"
      inventoryAdjustment: Pick<InventoryAdjustment, "inventoryAdjustmentId">
    }

interface UseCreateAdjustmentBaseParams<TMutation> {
  documentNode: Parameters<typeof useMutation>[0]
  getOutput: (data: TMutation) => AdjustmentCreateOutput | undefined
}

interface UseCreateAdjustmentBaseReturn<TInput> {
  createAdjustment: (input: TInput, comment?: string, document?: File) => Promise<boolean>
  isCreating: boolean
}

export const useCreateAdjustmentBase = <TMutation, TVariables extends { input: TInput }, TInput>({
  documentNode,
  getOutput,
}: UseCreateAdjustmentBaseParams<TMutation>): UseCreateAdjustmentBaseReturn<TInput> => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { createAdjustmentAttachments, isCreating: isCreatingAttachments } = useCreateAdjustmentAttachments()

  const [{ fetching: isCreatingAdjustment }, executeCreateAdjustment] = useMutation<TMutation, TVariables>(documentNode)

  const createAdjustment = useCallback(
    async (input: TInput, comment?: string, document?: File): Promise<boolean> => {
      const { data, error } = await executeCreateAdjustment({ input } as TVariables)
      const output = data ? getOutput(data) : undefined
      const hasError = error || output?.__typename === "CreateInventoryAdjustmentFailure"

      if (hasError) {
        const message = error?.message ?? (output as CreateInventoryAdjustmentFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      const adjustmentId = (output as CreateInventoryAdjustmentSuccess).inventoryAdjustment.inventoryAdjustmentId
      const isAttachmentSuccess = await createAdjustmentAttachments(adjustmentId, comment, document)

      if (!isAttachmentSuccess) return false

      notify({ type: NotificationType.Success, message: "Inventory adjustment created" })

      return true
    },
    [executeCreateAdjustment, getOutput, createAdjustmentAttachments, notify]
  )

  return { createAdjustment, isCreating: isCreatingAdjustment || isCreatingAttachments }
}
